import { SmartImage } from "@/components/site/SmartImage";
import { knowledgeImages } from "@/lib/responsive-images";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

import { CtaBand, PageHero } from "@/components/site/Page";
import { ResourceBody } from "@/components/site/ResourceGate";
import { articleJsonLd, seoLinks, ogImage, clampTitle, clampDescription } from "@/lib/seo";
import { resourceBySlug, resources } from "@/lib/resources";
import { getResourceMeta } from "@/lib/resources.functions";
import { safeLoader } from "@/lib/safe-loader";

export const Route = createFileRoute("/en/knowledge-hub/$slug")({
  loader: async ({ params }) => {
    const resource = resourceBySlug(params.slug);
    if (!resource || resource.slugEn !== params.slug) throw notFound();
    const meta = await safeLoader("en.resource-meta", () => getResourceMeta({ data: { slug: resource.slug, locale: "en" } }), null);
    return { slug: resource.slug, meta };
  },
  head: ({ params, loaderData }) => {
    const resource = resourceBySlug(params.slug);
    if (!resource) {
      return { meta: [{ title: "Page not found" }, { name: "robots", content: "noindex" },
        { property: "og:locale", content: "en_GB" },
        ...ogImage(),
      ] };
    }
    const title = clampTitle(loaderData?.meta?.title ?? resource.titleEn, "Knowledge hub");
    const description = clampDescription(loaderData?.meta?.description ?? resource.introEn);
    const path = `/en/knowledge-hub/${resource.slugEn}`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: seoLinks(path),
      scripts: [
        articleJsonLd({
          path,
           headline: loaderData?.meta?.title ?? resource.titleEn,
          description,
          image: resource.image,
          locale: "en",
        }),
      ],
    };
  },
  notFoundComponent: NotFoundView,
  component: Page,
});

function NotFoundView() {
  return (
    <>
      <PageHero
        eyebrow="Knowledge hub"
        title="This resource does not exist"
        intro="The link appears to be incorrect. Go back to the knowledge hub to see all material."
      />
      <div className="mx-auto max-w-3xl px-5 py-12">
        <Link to="/en/knowledge-hub" className="text-primary link-quiet">
          To the knowledge hub
        </Link>
      </div>
    </>
  );
}

function Page() {
  const { slug, meta } = Route.useLoaderData();
  const resource = resourceBySlug(slug)!;
  const others = resources.filter((r) => r.slug !== resource.slug);

  return (
    <>
      <PageHero
        eyebrow="Knowledge hub"
        breadcrumbLabel={meta?.title ?? resource.titleEn}
        title={meta?.title ?? resource.titleEn}
        intro={meta?.description ?? resource.introEn}
      />

      <div className="mx-auto max-w-3xl px-5 pt-10">
        <SmartImage
          image={knowledgeImages[resource.slug]!}
          alt={resource.titleEn}
          width={1280}
          height={720}
          sizes="(max-width: 768px) 100vw, 768px"
          className="aspect-[16/9] w-full rounded-xl bg-muted object-cover"
        />
      </div>

      <ResourceBody resource={resource} locale="en" />

      <section className="mx-auto max-w-3xl px-5 pb-12 sm:pb-16">
        <h2 className="text-lg font-semibold">More in the knowledge hub</h2>
        <ul className="mt-4 space-y-3">
          {others.map((r) => (
            <li key={r.slug}>
              <Link
                to="/en/knowledge-hub/$slug"
                params={{ slug: r.slugEn }}
                className="group flex items-center justify-between gap-4 rounded-xl border border-border bg-card p-4 transition-colors hover:border-primary/40"
              >
                <span className="min-w-0">
                  <span className="block font-medium">{r.titleEn}</span>
                  <span className="mt-1 block text-sm text-muted-foreground">
                    {r.minutes} min read
                  </span>
                </span>
                <ArrowRight
                  className="size-4 shrink-0 text-primary transition-transform group-hover:translate-x-0.5"
                  aria-hidden
                />
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <CtaBand locale="en" />
      <div className="h-16" />
    </>
  );
}
